import type { SetStateAction } from 'jotai'
import type { Dispatch } from 'react'
import { useTranslation } from 'react-i18next'
import FluentPremium24Regular from '~icons/fluent/premium-24-regular'
import IconoirBrightCrown from '~icons/iconoir/bright-crown'
import { openSignModal } from 'src/components/Account/utils'
import { MembershipIcon } from 'src/components/Membership/MembershipIcon'
import { UIButton } from 'src/components/ui/Button'
import Text from 'src/components/ui/Text'
import { useMe } from 'src/services/helpers/user'
import { cx } from 'src/utils/cx'
import { track } from 'src/utils/tracker'
import { MEMBERSHIP_BOOST_TIERS } from '../constants'

interface MembershipBoostSwitcherProps {
  tier: number
  onSelect: (tier: number) => void
  setMode: Dispatch<SetStateAction<'plans' | 'packs'>>
}

export function MembershipBoostSwitcher({
  tier,
  onSelect,
  setMode,
}: MembershipBoostSwitcherProps) {
  const { t } = useTranslation(['membership'])
  const { me, beingMember } = useMe()

  function proceedSubscribe() {
    track('enter_member_click', {
      location: 'credit-pack-boost-switcher',
      tier,
    })
    if (!me) {
      openSignModal()
      return
    }

    return setMode('plans')
  }

  const crownClassName = cx(
    'size-10',
    beingMember
      ? 'text-[--event-yellow]'
      : 'text-dspurple-500 dark:text-dspurple-400',
  )

  return (
    <section className="flex flex-col items-center gap-2">
      <IconoirBrightCrown className={cx(crownClassName, 'lg:hidden')} />
      <h3 className="text-xl md:text-2xl text-center text-neutral-900 dark:text-neutral-100 leading-tight">
        <Text
          i18nKey="membership:more-credits.boost.membership-upgrade.treatment.title"
          components={{
            strong: <strong className="font-bold" />,
          }}
          defaults="Get Up To <strong>150% More Credit Bonuses</strong> with Memberships!"
        />
      </h3>
      <p className="text-neutral-600 dark:text-neutral-400 mb-2 font-medium text-sm text-center">
        <Text
          i18nKey="membership:more-credits.boost.membership-upgrade.treatment.subtitle"
          defaults="Higher tiers give you more bonus credits on every purchase"
        />
      </p>
      <div className="flex flex-col lg:flex-row items-center justify-center font-inter mb-4 gap-3 lg:gap-6">
        <div className="hidden lg:flex flex-col items-center gap-2.5 lg:order-first">
          <IconoirBrightCrown className={crownClassName} />
          <span className="text-sm font-medium">
            <Text i18nKey="membership:more-credits.boost.membership-benefit" />
          </span>
        </div>

        <div className="flex gap-2 md:gap-4 lg:gap-6 mb-5 md:mb-0">
          {MEMBERSHIP_BOOST_TIERS.map(tierEntry => (
            <BoostSwitcherItem
              key={tierEntry.id}
              tier={tierEntry.tier}
              active={tier === tierEntry.tier}
              percentage={tierEntry.percentage}
              onClick={() => onSelect(tierEntry.tier)}
            />
          ))}
        </div>

        <div className="flex flex-col items-center gap-1">
          <UIButton
            className="w-full"
            icon={<FluentPremium24Regular />}
            color="theme"
            variant="filled"
            size="large"
            onPress={proceedSubscribe}
          >
            <Text
              i18nKey={
                tier === 0
                  ? 'membership:more-credits.boost.upgrade.treatment.label'
                  : 'membership:more-credits.boost.upgrade-to-tier.label'
              }
              values={{ tier: t(`membership:membership.tier.${tier}.label`) }}
              defaults="Upgrade to get more"
            />
          </UIButton>
          <span className="text-xs text-zinc-700 dark:text-zinc-400 font-inter font-semibold">
            <Text i18nKey="membership:more-credits.boost.upgrade.hint" />
          </span>
        </div>
      </div>
    </section>
  )
}

function BoostSwitcherItem({
  active,
  percentage,
  tier,
  onClick,
}: {
  active: boolean
  percentage: number
  tier: number
  onClick: () => void
}) {
  return (
    <button
      className={cx(
        'flex flex-col items-center justify-center size-20 md:size-24 bg-neutral-100 dark:bg-neutral-700 rounded-xl',
        active && 'ring-2 ring-dspurple-500',
      )}
      onClick={onClick}
    >
      <span
        className={cx(
          'text-center text-lg md:text-xl font-medium mt-2 mb-3',
          percentage > 0
            ? active
              ? 'text-[#621D9E] dark:text-dspurple-400'
              : 'text-dspurple-500 dark:text-dspurple-400/75'
            : 'text-neutral-500 dark:text-neutral-400',
        )}
      >
        +{percentage}%
      </span>
      <span
        className={cx(
          'flex items-center gap-1.5 text-xs md:text-sm font-medium',
          active
            ? 'text-dspurple-500 dark:text-dspurple-400'
            : 'text-stone-300',
        )}
      >
        <MembershipIcon className="size-3 md:size-4" />
        <Text i18nKey={`membership:membership.tier.${tier}.label`} />
      </span>
    </button>
  )
}
